/**
 * Every link that used to live only in the website footer. The footer (desktop/tablet) and the
 * Account → More screen (phones and the installed app) both render from here, so nothing is lost.
 */
export interface SiteLink {
  to: string;
  label: string;
  /** Opens outside the app (social profiles). */
  external?: boolean;
}

export interface SiteLinkGroup {
  id: "shop" | "help" | "company" | "legal";
  title: string;
  links: SiteLink[];
}

export const SITE_LINK_GROUPS: SiteLinkGroup[] = [
  {
    id: "shop",
    title: "Shop",
    links: [
      { to: "/search", label: "All categories" },
      { to: "/deals", label: "Today's deals" },
      { to: "/sell", label: "Sell on RealityStore" },
      { to: "/advertise", label: "Advertise with us" },
      { to: "/compare", label: "Compare" },
      { to: "/account/rewards", label: "Rewards & referrals" },
    ],
  },
  {
    id: "help",
    title: "Help",
    links: [
      { to: "/help", label: "Help centre" },
      { to: "/safety", label: "How RealitySafe works" },
      { to: "/safety#buyer-safety", label: "Buyer safety tips" },
      { to: "/support?topic=scam", label: "Report a scam" },
      { to: "/support", label: "Contact support" },
      { to: "/faq", label: "FAQs" },
    ],
  },
  {
    id: "company",
    title: "Company",
    links: [
      { to: "/about", label: "About RealityStore" },
      { to: "/c/jobs", label: "Careers" },
      { to: "/news", label: "Blog & news" },
    ],
  },
  {
    id: "legal",
    title: "Legal",
    links: [
      { to: "/terms", label: "Terms of use" },
      { to: "/privacy", label: "Privacy policy (NDPA 2023)" },
      { to: "/refund-policy", label: "Refund & dispute policy" },
      { to: "/vendor-agreement", label: "Vendor agreement" },
      { to: "/cookies", label: "Cookie policy" },
      { to: "/ad-policy", label: "Advertising policy" },
    ],
  },
];

/** CMS slugs the app routes to `StaticPage` (Admin → Content → Pages edits them). */
export const STATIC_PAGE_SLUGS = [
  "about",
  "terms",
  "privacy",
  "safety",
  "help",
  "ad-policy",
  "refund-policy",
  "vendor-agreement",
  "cookies",
  "news",
] as const;

export type SocialNetwork = "tiktok" | "instagram" | "x" | "facebook" | "linkedin" | "youtube" | "telegram";

export const SOCIAL_LABELS: Record<SocialNetwork, string> = {
  tiktok: "TikTok",
  instagram: "Instagram",
  x: "X",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  telegram: "Telegram Deals",
};

/** Social profiles come from VITE_SOCIAL_<NETWORK> (unset networks are simply not shown). */
export function socialLinks(env: Record<string, string | undefined> = import.meta.env): { network: SocialNetwork; url: string }[] {
  return (Object.keys(SOCIAL_LABELS) as SocialNetwork[])
    .map((network) => ({ network, url: env[`VITE_SOCIAL_${network.toUpperCase()}`] ?? "" }))
    .filter((s) => /^https:\/\//.test(s.url));
}

/** "v1.8.0 (build 241)" from the build (see vite.config.ts). */
export function appVersionLabel(env: Record<string, string | undefined> = import.meta.env): string {
  const version = env.VITE_APP_VERSION || "0.0.0";
  const build = env.VITE_APP_BUILD;
  return build ? `v${version} (build ${build})` : `v${version}`;
}
